import React from 'react';

import {PartJSON} from 'biojs/Part';
import {CustomTag, isSchemaTag, SchemaTag, Tag} from 'folder-items/FileConstants';
import {getSchemaFieldArchivableItem} from 'schemas/fields/isSchemaFieldArchived';
import {FIELD_TYPES} from 'schemas/fields/SchemaFieldConstants';
import SchemaFieldName from 'schemas/fields/SchemaFieldName';
import * as TagUtil from 'schemas/tags/TagUtil';
import getTestProps from 'test/lib/cypress/getTestProps';
import FormInput from 'ui-library/bs/FormInput';
import {ElementKeyboardEvent, Optional} from 'util/Types';

export interface TagNameCellProps {
  isEditing: boolean;
  isNameEditable: boolean;
  onKeyDown: (e: ElementKeyboardEvent) => void;
  onUpdateTag: (tagIndex: number, newValue: Tag) => void;
  sequenceParts: Optional<Array<PartJSON>>;
  shouldAutoFocus: boolean;
  tagIndex: number;
  tag: SchemaTag | CustomTag;
}

const getMaxDepthPartLinkInSequence = (
  tag: SchemaTag,
  sequence: Optional<{id?: string; parts?: Array<PartJSON>}>,
  currentDepth: number
): number => {
  if (sequence == null) {
    return 0;
  }
  let retVals = [];
  if (tag.targetFiles!.some((targetFile) => targetFile.id === sequence.id)) {
    retVals = [currentDepth];
  } else {
    retVals = [0];
  }

  if (sequence.parts != null) {
    retVals.push(
      ...sequence.parts.map((p: PartJSON) => getMaxDepthPartLinkInSequence(tag, p.sequence, currentDepth + 1))
    );
  }
  return Math.max(...retVals);
};

class TagNameCell extends React.Component<TagNameCellProps> {
  private handleChangeName = (e: React.ChangeEvent<HTMLInputElement>): void => {
    this.props.onUpdateTag(this.props.tagIndex, {name: e.target.value});
  };

  renderTagName = (): React.ReactNode => {
    if (!isSchemaTag(this.props.tag)) {
      return this.props.tag.name;
    }

    if (
      TagUtil.getFullFieldType(this.props.tag.tagSchemaField.fieldType) !== FIELD_TYPES.PART_LINK ||
      this.props.tag.targetFiles!.length === 0
    ) {
      return <SchemaFieldName field={getSchemaFieldArchivableItem(this.props.tag.tagSchemaField)} />;
    }
    // Special rendering for part links
    const maxDepth = getMaxDepthPartLinkInSequence(
      this.props.tag,
      {parts: this.props.sequenceParts ?? []},
      0
    );
    return (
      <span>
        {maxDepth > 0 && `${'\u21b3'.repeat(maxDepth - 1)} `}
        <SchemaFieldName field={getSchemaFieldArchivableItem(this.props.tag.tagSchemaField)} />
      </span>
    );
  };

  override render(): JSX.Element {
    return (
      <div
        className="u-cursor-text"
        {...getTestProps({
          component: 'TagNameCell',
          element: 'root',
          index: String(this.props.tagIndex),
        })}
        data-amp-safe={false}
      >
        {this.props.isEditing && this.props.isNameEditable ? (
          <FormInput
            autoFocus={this.props.shouldAutoFocus}
            placeholder="Name (required)"
            value={this.props.tag.name}
            onChange={this.handleChangeName}
            onKeyDown={this.props.onKeyDown}
            onFocus={(e) => {
              const input = e.target;
              input.setSelectionRange(input.value.length, input.value.length);
            }}
            {...getTestProps({
              component: 'TagNameCell',
              element: 'input',
              index: String(this.props.tagIndex),
            })}
            data-amp-safe={false}
          />
        ) : (
          this.renderTagName()
        )}
      </div>
    );
  }
}

export default TagNameCell;
